import { useMemo, useState } from 'react';

import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import { Card, LoadError, Pagination } from '~/components/ui';
import { StatTile, StatTileSkeleton, StatsRow } from '~/components/dashboard';
import { useListUrlState } from '~/hooks/useListUrlState';
import { matchesFeedFilters, matchesFeedSearch } from '~/lib/feedFilters';
import { clampPage, pageCount, splitValues } from '~/lib/listParams';
import { queryErrorMessage } from '~/modules/query';
import { useGetMlsQuery, useGetMlsStatsQuery } from '~/modules/query/mls';
import MLSTable from '~/components/pages/mls/components/mlsTable';
import { MLS_STAT_TITLES, type MlsRow } from '~/components/Constants/mlsConstants';
import MlsDeleteConfirm from '~/components/pages/mls/components/mlsDeleteConfirm';
import MlsDrawer, { FEED_FILTER_FIELDS } from '~/components/pages/mls/components/mlsDrawer';

const FILTER_KEYS = FEED_FILTER_FIELDS.map((field) => field.param);

export const MLS = () => {
  const list = useListUrlState({ filterKeys: FILTER_KEYS });
  const {
    data: mlsList,
    isLoading: listLoading,
    isFetching: listFetching,
    error: listError,
    refetch: refetchList,
  } = useGetMlsQuery();
  const {
    data: mlsStats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useGetMlsStatsQuery();
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MlsRow | null>(null);

  const rows = useMemo(() => (mlsList ?? []) as MlsRow[], [mlsList]);

  const filteredRows = useMemo(() => {
    const chosen = Object.fromEntries(
      FEED_FILTER_FIELDS.map(({ param, field }) => [field, splitValues(list.filters[param])]),
    );
    return rows.filter(
      (row) => matchesFeedFilters(row, chosen) && (!list.q || matchesFeedSearch(row, list.q)),
    );
  }, [rows, list.filters, list.q]);

  const pages = pageCount(filteredRows.length, list.pageSize);
  const page = clampPage(list.page, filteredRows.length, list.pageSize);
  const pageRows = filteredRows.slice((page - 1) * list.pageSize, page * list.pageSize);

  return (
    <PageShell>
      <PageHeader
        title="Feeds"
        description="The feed services we integrate with, the platforms they connect to and their statistics."
        breadcrumbs={[{ label: 'Feeds' }]}
      />

      {statsError && (
        <LoadError
          what="the feed stats"
          message={queryErrorMessage(statsError)}
          onRetry={refetchStats}
          testId="feeds-stats"
        />
      )}

      <StatsRow ariaLabel="Feed statistics">
        {Object.keys(MLS_STAT_TITLES).map((key) =>
          statsLoading ? (
            <StatTileSkeleton key={key} />
          ) : (
            <StatTile
              key={key}
              label={MLS_STAT_TITLES[key]}
              value={mlsStats?.[key] as string | number | null | undefined}
            />
          ),
        )}
      </StatsRow>

      <Card>
        <ListToolbar
          title="Feeds"
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search feeds…',
            testId: 'feeds-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setShowFilterDrawer(true),
            onClear: list.clear,
            testId: 'feeds-filter-button',
          }}
        />
        <MLSTable
          data={pageRows}
          listLoad={listLoading || listFetching}
          listError={
            listError
              ? { what: 'the feeds', message: queryErrorMessage(listError), onRetry: refetchList }
              : undefined
          }
          filtered={Boolean(list.q) || list.filterCount > 0}
          onDelete={setDeleteTarget}
        />
        <Pagination
          pageIndex={page - 1}
          pageSize={list.pageSize}
          pageCount={pages}
          totalRows={filteredRows.length}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <MlsDrawer
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
        rows={rows}
      />

      <MlsDeleteConfirm feed={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </PageShell>
  );
};
